import React, { useState, useEffect, useRef, useCallback } from 'react';
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import {
    FormControl,
    FormLabel,
    Input,
    Select,
    Textarea,
    HStack,
    VStack,
    Text,
    Box,
    Alert,
    AlertIcon,
    Spinner,
    Center,
    Button,
    IconButton,
    useToast,
    Badge,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Icon,
    SimpleGrid,
    Checkbox,
} from '@chakra-ui/react';
import { supabase } from './utils/supabaseClient';
import { shouldShowOnCorporateEnrollForm, isSignaturePadField } from './corporate-enroll/corporateEnrollHelpers';
import SignaturePad from 'react-signature-canvas';
import { DeleteIcon, AddIcon, AttachmentIcon, CheckCircleIcon, CloseIcon } from '@chakra-ui/icons';
import RegistrationSearchableSelect from './components/RegistrationSearchableSelect';
import countryList from 'react-select-country-list';
import {
    valuesEqualCountryAware,
    normalizeConditionalRequirements,
} from './utils/conditionalFieldValues';

/** HTML body for `declaration` / `declaration_text` — field_options is typically `["<p>…</p>", "html"]`. */
function getDeclarationHtmlFromFieldOptions(fieldOptions) {
    if (fieldOptions == null || fieldOptions === '') return '';
    let opts = fieldOptions;
    if (typeof opts === 'string') {
        try {
            opts = JSON.parse(opts);
        } catch {
            return opts;
        }
    }
    if (Array.isArray(opts)) {
        const first = opts[0];
        return typeof first === 'string' ? first : '';
    }
    if (typeof opts === 'object' && opts !== null && typeof opts.html === 'string') {
        return opts.html;
    }
    return '';
}

/** Corporate enrol: canvas signature + companion keys (`date_signed`, `*_timestamp`), aligned with TMS draft forms. */
function CorporateSignatureField({ field, fieldId, fieldValue, fieldError, isReadOnly, handleFieldChange, handleFieldBlur }) {
    const sigRef = useRef(null);
    const lastEmittedRef = useRef('');

    const applyCompanion = useCallback(
        (timestampIso) => {
            if (field.field_key === 'signature') {
                handleFieldChange('date_signed', timestampIso ? timestampIso.slice(0, 10) : '');
            } else if (field.field_key.endsWith('_url')) {
                handleFieldChange(field.field_key.replace('_url', '_timestamp'), timestampIso || '');
            }
        },
        [field.field_key, handleFieldChange],
    );

    const onEnd = useCallback(() => {
        const pad = sigRef.current;
        if (!pad || pad.isEmpty()) return;
        const dataUrl = pad.getTrimmedCanvas().toDataURL('image/png');
        lastEmittedRef.current = dataUrl;
        handleFieldChange(field.field_key, dataUrl);
        applyCompanion(new Date().toISOString());
        handleFieldBlur(field, dataUrl);
    }, [field, handleFieldChange, handleFieldBlur, applyCompanion]);

    const clear = useCallback(() => {
        sigRef.current?.clear();
        lastEmittedRef.current = '';
        handleFieldChange(field.field_key, '');
        applyCompanion('');
        handleFieldBlur(field, '');
    }, [field, handleFieldChange, handleFieldBlur, applyCompanion]);

    useEffect(() => {
        if (!fieldValue || typeof fieldValue !== 'string' || !fieldValue.startsWith('data:image')) return;
        if (fieldValue === lastEmittedRef.current) return;
        const pad = sigRef.current;
        if (!pad) return;
        try {
            pad.clear();
            pad.fromDataURL(fieldValue);
            lastEmittedRef.current = fieldValue;
        } catch (e) {
            console.warn('Could not load signature image', e);
        }
    }, [fieldValue]);

    if (isReadOnly && fieldValue && typeof fieldValue === 'string' && fieldValue.startsWith('data:image')) {
        return (
            <Box borderWidth="1px" borderRadius="md" p={3} bg="gray.50">
                <Text fontSize="xs" mb={2} color="gray.600">
                    Saved signature
                </Text>
                <img src={fieldValue} alt="" style={{ maxWidth: '100%', height: 'auto' }} />
            </Box>
        );
    }
    if (isReadOnly) {
        return (
            <Text fontSize="sm" color="gray.500">
                No signature on file
            </Text>
        );
    }

    const hasSig = fieldValue && String(fieldValue).startsWith('data:image');

    return (
        <VStack id={fieldId} align="stretch" spacing={3} w="100%">
            {/*
              Do not pass width/height on canvasProps: react-signature-canvas only measures
              offsetWidth/offsetHeight when those props are omitted. Values like width: '100%'
              are truthy so the pad skips auto-size and ends up squeezed (invalid canvas width).
            */}
            <Box
                w="100%"
                minW={0}
                h="200px"
                borderWidth="2px"
                borderRadius="md"
                borderColor={fieldError ? 'red.300' : hasSig ? 'green.400' : 'gray.200'}
                bg="white"
                overflow="hidden"
            >
                <SignaturePad
                    ref={sigRef}
                    canvasProps={{
                        className: 'signatureCanvas',
                        style: {
                            width: '100%',
                            height: '100%',
                            display: 'block',
                            verticalAlign: 'top',
                            touchAction: 'none',
                        },
                    }}
                    onEnd={onEnd}
                    clearOnResize={false}
                />
            </Box>
            <HStack spacing={3} flexWrap="wrap">
                <Button size="sm" variant="outline" onClick={clear} type="button">
                    Clear
                </Button>
                {hasSig ? (
                    <Text fontSize="xs" color="green.600" fontWeight="medium">
                        Signature captured — you can clear and sign again if needed
                    </Text>
                ) : (
                    <Text fontSize="xs" color="gray.500">
                        Sign in the box using your mouse or touch
                    </Text>
                )}
            </HStack>
        </VStack>
    );
}

/** Renders a label that may contain a "//" separator into primary + secondary lines.
 *  e.g. "中文//English" → "中文" (bold) with "English" below (italic, smaller, muted). Single "/" is literal. */
function BilingualLabel({ label, isRequired }) {
    const delim = '//';
    const slashIdx = label ? label.indexOf(delim) : -1;
    if (slashIdx === -1) {
        return (
            <>
                {label}
                {isRequired && <Text as="span" color="red.500" ml={1}>*</Text>}
            </>
        );
    }
    const primary = label.slice(0, slashIdx).trim();
    const secondary = label.slice(slashIdx + delim.length).trim();
    return (
        <Box lineHeight="tight">
            <Text as="span" fontWeight="bold">
                {primary}
                {isRequired && <Text as="span" color="red.500" ml={1}>*</Text>}
            </Text>
            <Text fontSize="xs" fontWeight="normal" fontStyle="italic" color="gray.500" mt={0.5}>
                {secondary}
            </Text>
        </Box>
    );
}

function getFileIcon(filename) {
    const ext = (filename || '').split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'heic'].includes(ext)) return '🖼️';
    if (ext === 'pdf') return '📄';
    if (['doc', 'docx'].includes(ext)) return '📝';
    if (['xls', 'xlsx'].includes(ext)) return '📊';
    if (['zip', 'rar', '7z'].includes(ext)) return '🗜️';
    return '📎';
}

function formatFileSize(bytes) {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FileUploadZone({
    field,
    fieldValue,
    isReadOnly,
    isUploading,
    selectedFilesForField,
    onFileSelect,
    onRemoveStaged,
    onRemoveUploaded,
    formData,
    toast,
}) {
    const [isDragOver, setIsDragOver] = React.useState(false);
    const inputRef = React.useRef(null);
    const isMultiple = field.field_type === 'file_multiple';
    const hasUploaded = fieldValue && (Array.isArray(fieldValue) ? fieldValue.length > 0 : fieldValue !== '');
    const hasStaged = selectedFilesForField && selectedFilesForField.length > 0;

    const triggerPicker = () => {
        if (!formData.participant_name || formData.participant_name.trim() === '') {
            toast({
                title: 'Enter participant name first',
                description: 'A name is needed to create the upload record.',
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });
            return;
        }
        if (inputRef.current) inputRef.current.click();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        if (isReadOnly || isUploading) return;
        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            if (!formData.participant_name || formData.participant_name.trim() === '') {
                toast({ title: 'Enter participant name first', status: 'warning', duration: 3000, isClosable: true });
                return;
            }
            onFileSelect(files);
        }
    };

    const uploadedFiles = hasUploaded
        ? (Array.isArray(fieldValue) ? fieldValue : [fieldValue])
        : [];

    return (
        <Box>
            <input
                ref={inputRef}
                type="file"
                multiple={isMultiple}
                onChange={(e) => onFileSelect(e.target.files)}
                style={{ display: 'none' }}
                accept="image/*,.pdf,.doc,.docx,.txt,.xls,.xlsx,.zip"
                disabled={isReadOnly}
            />

            {/* Drop zone — always visible when editable, shows "Change" state when file already uploaded */}
            {!isReadOnly && (
                <Box
                    onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                    onDragLeave={() => setIsDragOver(false)}
                    onDrop={handleDrop}
                    onClick={!isUploading ? triggerPicker : undefined}
                    cursor={isUploading ? 'not-allowed' : 'pointer'}
                    border="2px dashed"
                    borderColor={isDragOver ? 'blue.400' : hasUploaded ? 'green.300' : 'gray.300'}
                    borderRadius="lg"
                    bg={isDragOver ? 'blue.50' : hasUploaded ? 'green.50' : 'gray.50'}
                    p={5}
                    textAlign="center"
                    transition="all 0.15s ease"
                    _hover={{ borderColor: 'blue.300', bg: 'blue.50' }}
                    role="button"
                    aria-label="Upload file"
                >
                    {isUploading ? (
                        <VStack spacing={2}>
                            <Spinner size="md" color="blue.500" />
                            <Text fontSize="sm" color="blue.600" fontWeight="medium">Uploading…</Text>
                        </VStack>
                    ) : hasUploaded && !isMultiple ? (
                        <VStack spacing={1}>
                            <CheckCircleIcon boxSize={6} color="green.500" />
                            <Text fontSize="sm" color="green.700" fontWeight="medium">File uploaded</Text>
                            <Text fontSize="xs" color="gray.500">Click to replace</Text>
                        </VStack>
                    ) : (
                        <VStack spacing={2}>
                            <Box fontSize="2xl">{isDragOver ? '📂' : '📁'}</Box>
                            <Text fontSize="sm" fontWeight="semibold" color={isDragOver ? 'blue.600' : 'gray.700'}>
                                {isDragOver
                                    ? 'Drop files here'
                                    : isMultiple
                                        ? 'Drag & drop files, or click to browse'
                                        : 'Drag & drop a file, or click to browse'}
                            </Text>
                        </VStack>
                    )}
                </Box>
            )}

            {/* Staged files (selected but not yet submitted) */}
            {hasStaged && (
                <Box mt={3} border="1px solid" borderColor="blue.200" borderRadius="md" overflow="hidden">
                    <Box bg="blue.50" px={3} py={1.5}>
                        <Text fontSize="xs" fontWeight="bold" color="blue.700" textTransform="uppercase" letterSpacing="wide">
                            Ready to upload · {selectedFilesForField.length} {selectedFilesForField.length === 1 ? 'file' : 'files'}
                        </Text>
                    </Box>
                    <VStack spacing={0} align="stretch" divider={<Box h="1px" bg="gray.100" />}>
                        {Array.from(selectedFilesForField).map((file, index) => (
                            <HStack key={index} px={3} py={2} justify="space-between" bg="white" _hover={{ bg: 'gray.50' }}>
                                <HStack spacing={2} flex={1} minW={0}>
                                    <Text fontSize="lg" flexShrink={0}>{getFileIcon(file.name)}</Text>
                                    <Box minW={0}>
                                        <Text fontSize="sm" isTruncated fontWeight="medium" color="gray.800">{file.name}</Text>
                                        <Text fontSize="xs" color="gray.400">{formatFileSize(file.size)}</Text>
                                    </Box>
                                </HStack>
                                <IconButton
                                    size="xs"
                                    icon={<CloseIcon boxSize="8px" />}
                                    colorScheme="red"
                                    variant="ghost"
                                    aria-label="Remove file"
                                    borderRadius="full"
                                    onClick={(e) => { e.stopPropagation(); onRemoveStaged(index); }}
                                />
                            </HStack>
                        ))}
                    </VStack>
                    <Box bg="blue.50" px={3} py={1.5}>
                        <Text fontSize="10px" color="blue.600" fontStyle="italic">
                            Will be saved when you submit the form
                        </Text>
                    </Box>
                </Box>
            )}

            {/* Already uploaded files */}
            {uploadedFiles.length > 0 && (
                <Box mt={3} border="1px solid" borderColor="green.200" borderRadius="md" overflow="hidden">
                    <Box bg="green.50" px={3} py={1.5}>
                        <HStack spacing={1}>
                            <CheckCircleIcon boxSize={3} color="green.600" />
                            <Text fontSize="xs" fontWeight="bold" color="green.700" textTransform="uppercase" letterSpacing="wide">
                                Uploaded · {uploadedFiles.length} {uploadedFiles.length === 1 ? 'file' : 'files'}
                            </Text>
                        </HStack>
                    </Box>
                    <VStack spacing={0} align="stretch" divider={<Box h="1px" bg="gray.100" />}>
                        {uploadedFiles.map((filePath, index) => {
                            const fileName = filePath.split('/').pop();
                            const { data: urlData } = supabase.storage
                                .from('Registration_data')
                                .getPublicUrl(filePath);
                            return (
                                <HStack key={index} px={3} py={2} justify="space-between" bg="white" _hover={{ bg: 'gray.50' }}>
                                    <HStack spacing={2} flex={1} minW={0}>
                                        <Text fontSize="lg" flexShrink={0}>{getFileIcon(fileName)}</Text>
                                        <Text
                                            fontSize="sm"
                                            isTruncated
                                            color="blue.600"
                                            fontWeight="medium"
                                            as="a"
                                            href={urlData?.publicUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            _hover={{ textDecoration: 'underline' }}
                                            flex={1}
                                            minW={0}
                                        >
                                            {fileName}
                                        </Text>
                                    </HStack>
                                    {!isReadOnly && (
                                        <IconButton
                                            size="xs"
                                            icon={<CloseIcon boxSize="8px" />}
                                            colorScheme="red"
                                            variant="ghost"
                                            aria-label="Remove uploaded file"
                                            borderRadius="full"
                                            onClick={(e) => { e.stopPropagation(); onRemoveUploaded(index); }}
                                        />
                                    )}
                                </HStack>
                            );
                        })}
                    </VStack>
                </Box>
            )}
        </Box>
    );
}

const AdditionalFields = ({ 
    formData, 
    setFormData, 
    validationErrors, 
    setValidationErrors, 
    registrationId, 
    setRegistrationId, 
    isReadOnly = false, 
    onAutoSave,
    templateFieldIds = null,
    selectedFiles,
    setSelectedFiles,
    uploadingFiles,
    setUploadingFiles,
    /** When true (corporate self-enrol), show template-selected original + compulsory-section fields on one page */
    corporateEnrollMode = false,
    /** Increment to run full validation on all visible fields (corporate enrol submit) */
    corporateValidateTick = 0,
    /** Called with true if no visible field failed validateField */
    corporateOnValidateComplete = null,
    /** Called once when the field catalogue has finished loading (success or error). */
    onFieldsLoaded = null,
}) => {
    const [additionalFields, setAdditionalFields] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isCreatingDraft, setIsCreatingDraft] = useState(false);
    const [activeOtherFields, setActiveOtherFields] = useState(new Set());
    // Track which fields have been touched (focused and blurred)
    const [touchedFields, setTouchedFields] = useState(new Set());
    
    // Get country list
    const countryOptions = countryList().getData();
    // Cache a stable folder path for full certification uploads per session
    const fullCertFolderPathRef = useRef(null);
    const toast = useToast();

    // Section configuration - based on database sections
    const getSectionConfig = (sectionName) => {
        const configs = {
            'Compulsory fields': {
                icon: '⭐',
                color: 'red',
                bgColor: 'red.50',
                borderColor: 'red.200'
            },
            'Personal Details': {
                icon: '👤',
                color: 'green',
                bgColor: 'green.50',
                borderColor: 'green.200'
            },
            'Education': {
                icon: '🎓',
                color: 'purple',
                bgColor: 'purple.50',
                borderColor: 'purple.200'
            },
            'Work': {
                icon: '💼',
                color: 'orange',
                bgColor: 'orange.50',
                borderColor: 'orange.200'
            },
            'Programme Selection': {
                icon: '📚',
                color: 'blue',
                bgColor: 'blue.50',
                borderColor: 'blue.200'
            },
            'Additional Information': {
                icon: '📝',
                color: 'gray',
                bgColor: 'gray.50',
                borderColor: 'gray.200'
            },
            Declaration: {
                icon: '✓',
                color: 'teal',
                bgColor: 'teal.50',
                borderColor: 'teal.200'
            }
        };
        return configs[sectionName] || configs['Additional Information'];
    };

    // Use the database section directly - no hardcoded mapping needed
    const mapToSimplifiedSection = (originalSection) => {
        // Use the section from the database directly
        return originalSection || 'Additional Information';
    };

    useEffect(() => {
        fetchAdditionalFields();
    }, []);

    // Re-validate fields with conditional requirements when formData changes
    useEffect(() => {
        if (additionalFields.length === 0) return;
        
        // Get all trigger field keys that could affect conditional requirements
        const triggerFields = new Set();
        additionalFields.forEach(field => {
            if (field.conditional_requirements?.required_when?.field) {
                triggerFields.add(field.conditional_requirements.required_when.field);
            }
        });

        // Re-validate all fields that have conditional requirements
        const fieldsToRevalidate = additionalFields.filter(field => 
            field.conditional_requirements && 
            field.conditional_requirements.required_when
        );

        setValidationErrors(prev => {
            const newErrors = { ...prev };
            let hasChanges = false;

            fieldsToRevalidate.forEach(field => {
                // Check if field should be visible
                const isVisible = shouldShowField(field);
                
                // If field is not visible, clear its validation error
                if (!isVisible && prev[field.field_key]) {
                    newErrors[field.field_key] = false;
                    hasChanges = true;
                    return;
                }
                
                // Only validate visible fields
                if (isVisible) {
                    const fieldValue = getFieldValue(field.field_key);
                    const error = validateField(field, fieldValue);
                    const currentError = prev[field.field_key];
                    
                    // Only update if error state changed
                    if (error !== currentError) {
                        newErrors[field.field_key] = error || false;
                        hasChanges = true;
                    }
                }
            });

            return hasChanges ? newErrors : prev;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData]); // Only depend on formData - additionalFields is stable after initial load

    // Create draft registration for full certifications (needed for file uploads)
    const createDraftRegistration = async () => {
        if (registrationId || isCreatingDraft) return;
        
        if (!formData.participant_name || formData.participant_name.trim() === '') {
            return;
        }

        // Check if this is a full certification registration
        const urlParams = new URLSearchParams(window.location.search);
        const isFullCertification = urlParams.get('full_certification');
        const fullCertificationId = urlParams.get('full_certification');
        const certificationRunId = urlParams.get('certification_run_id');
        
        // Only create draft for full certifications (individual courses don't need it)
        if (!isFullCertification || !fullCertificationId || !certificationRunId) {
            return;
        }

        setIsCreatingDraft(true);
        try {
            const draftData = {
                participant_name: formData.participant_name,
                registration_stage: "draft",
                // Add full certification context for consistent folder naming
                full_certification_id: fullCertificationId,
                certification_run_id: certificationRunId,
                participant_email: formData.participant_email || null
            };

            const { data, error } = await supabase
                .from('registration')
                .insert([draftData])
                .select("id")
                .single();

            if (error) {
                console.error('Error creating draft registration:', error);
                toast({
                    title: "Error creating registration",
                    description: "Please try again later",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            } else {
                setRegistrationId(data.id);
                // Silent success - no toast message
            }
        } catch (error) {
            console.error('Error creating draft registration:', error);
        } finally {
            setIsCreatingDraft(false);
        }
    };

    // Watch for participant name changes (only for full certifications)
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const isFullCertification = urlParams.get('full_certification');
        
        if (isFullCertification && formData.participant_name && formData.participant_name.trim() !== '' && !registrationId) {
            createDraftRegistration();
        }
    }, [formData.participant_name, registrationId]);

    // Helper function to get nested field value from formData
    const getNestedValue = (data, fieldKey) => {
        // First check if it's a direct key
        if (data[fieldKey] !== undefined && data[fieldKey] !== null && data[fieldKey] !== '') {
            return data[fieldKey];
        }
        
        // Then check for nested keys (e.g., "personal_details.gender")
        if (fieldKey.includes('.')) {
            const keys = fieldKey.split('.');
            let current = data;
            for (const key of keys) {
                if (current && typeof current === 'object' && key in current) {
                    current = current[key];
                } else {
                    return '';
                }
            }
            return current || '';
        }
        
        return '';
    };

    // Check if a field is conditionally required based on conditional_requirements
    const isFieldConditionallyRequired = (field) => {
        if (!field.conditional_requirements) {
            return false; // No conditional requirements
        }

        const condition = field.conditional_requirements.required_when;
        if (!condition) {
            return false;
        }

        const triggerField = condition.field;
        const triggerValue = getNestedValue(formData, triggerField);
        const operator = condition.operator;
        const expectedValue = condition.value;

        switch (operator) {
            case 'equals':
                return valuesEqualCountryAware(triggerValue, expectedValue);
            case 'contains':
                return (
                    Array.isArray(triggerValue) &&
                    triggerValue.some((tv) => valuesEqualCountryAware(tv, expectedValue))
                );
            case 'in':
                return (
                    Array.isArray(expectedValue) &&
                    expectedValue.some((ev) => valuesEqualCountryAware(triggerValue, ev))
                );
            case 'not_equals':
                return !valuesEqualCountryAware(triggerValue, expectedValue);
            default:
                return false;
        }
    };

    /** True if the trigger field exists in additional_registration_fields and is part of the current template (when a template applies). */
    const isTriggerFieldIncludedInTemplate = (triggerFieldKey) => {
        if (!triggerFieldKey) return false;
        const triggerField = additionalFields.find((f) => f.field_key === triggerFieldKey);
        if (!triggerField) return false;
        if (templateFieldIds === null || templateFieldIds.length === 0) {
            return true;
        }
        return templateFieldIds.includes(triggerField.id);
    };

    // Get effective requirement status (static or conditional)
    const getEffectiveRequiredStatus = (field) => {
        const cr = field.conditional_requirements;
        if (cr?.required_when) {
            const triggerKey = cr.required_when.field;
            if (!isTriggerFieldIncludedInTemplate(triggerKey)) {
                return true;
            }
            return isFieldConditionallyRequired(field);
        }
        return field.is_required;
    };

    // Check if a field is required and unfilled (only show red border when there's a validation error)
    const isFieldRequiredAndUnfilled = (field) => {
        const isRequired = getEffectiveRequiredStatus(field);
        if (!isRequired) return false;
        
        // Only show red border if there's an actual validation error, not just because it's empty
        return validationErrors[field.field_key] || false;
    };

    // Check if a field is empty (for progress tracking)
    const isFieldEmpty = (field) => {
        if (field.field_type === 'declaration_text') {
            return false;
        }
        if (isSignaturePadField(field)) {
            const v = getFieldValue(field.field_key);
            return !v || !String(v).startsWith('data:image');
        }
        const fieldValue = getFieldValue(field.field_key);
        return !fieldValue || fieldValue === '' || (Array.isArray(fieldValue) && fieldValue.length === 0);
    };

    const fetchAdditionalFields = async () => {
        try {
            setLoading(true);
            // Fetch ALL active fields (both system and custom)
            // System fields will be shown in Compulsory fields section
            // Custom fields will be shown in their assigned sections
            const { data, error } = await supabase
                .from('additional_registration_fields')
                .select('*')
                .or('field_status.is.null,field_status.eq.active')
                // Remove filter to include system fields for Compulsory fields section
                // .eq('is_original_field', false)  // ❌ REMOVED
                .order('section_order', { ascending: true })
                .order('display_order', { ascending: true });

            if (error) throw error;
            
            // Parse field_behaviors if it's a string (JSONB from database)
            const processedData = (data || []).map(field => {
                let fieldBehaviors = field.field_behaviors;
                if (typeof fieldBehaviors === 'string') {
                    try {
                        fieldBehaviors = JSON.parse(fieldBehaviors);
                    } catch (e) {
                        console.warn('Failed to parse field_behaviors for field:', field.field_key, e);
                        fieldBehaviors = null;
                    }
                }
                
                // If this field is missing from formData, set its default value
                const currentValue = getNestedValue(formData, field.field_key);
                if (field.default_value && (currentValue === undefined || currentValue === null || currentValue === '')) {
                    setTimeout(() => {
                        handleFieldChange(field.field_key, field.default_value);
                    }, 0);
                }

                return {
                    ...field,
                    field_behaviors: fieldBehaviors,
                    conditional_requirements: normalizeConditionalRequirements(field.conditional_requirements),
                };
            });
            
            setAdditionalFields(processedData);
        } catch (err) {
            console.error('Error fetching additional fields:', err);
            setError('Failed to load additional fields');
        } finally {
            setLoading(false);
            onFieldsLoaded?.();
        }
    };

    const handleFieldChange = (fieldKey, value) => {
        console.log('handleFieldChange', fieldKey, value);
        setFormData(prevData => {
            const newData = { ...prevData };
            
            // IMPORTANT: All fields in AdditionalFields component are custom fields (is_original_field = false)
            // They should be saved to additional_data JSONB column, NOT registration table columns
            // System fields (is_original_field = true) are handled in the main registration form
            
            // Handle nested field keys (e.g., "personal_details.gender")
            if (fieldKey.includes('.')) {
                const keys = fieldKey.split('.');
                let current = newData;
                for (let i = 0; i < keys.length - 1; i++) {
                    if (!current[keys[i]]) {
                        current[keys[i]] = {};
                    }
                    current = current[keys[i]];
                }
                current[keys[keys.length - 1]] = value;
            } else {
                // For custom fields, store in additional_data structure
                // The parent component (registration form) will handle saving to additional_data
                newData[fieldKey] = value;
            }
            
            return newData;
        });

        // Clear validation error for this field
        setValidationErrors(prev => ({
            ...prev,
            [fieldKey]: false
        }));

        // Re-validate all fields with conditional requirements that depend on this field
        // This ensures that when a trigger field changes, dependent fields are re-validated
        setTimeout(() => {
            const dependentFields = additionalFields.filter(field => 
                field.conditional_requirements?.required_when?.field === fieldKey
            );
            
            dependentFields.forEach(field => {
                // Only update validation for touched fields
                if (touchedFields.has(field.field_key)) {
                    const fieldValue = getFieldValue(field.field_key);
                    const error = validateField(field, fieldValue);
                    setValidationErrors(prev => ({
                        ...prev,
                        [field.field_key]: error
                    }));
                }
            });
        }, 0);
    };

    const validateField = (field, value) => {
        // Check if field is required (static or conditional)
        const isRequired = getEffectiveRequiredStatus(field);
        
        // Check if there are staged files for this field (for file types)
        const stagedFiles = selectedFiles[field.field_key];
        const hasStagedFiles = stagedFiles && stagedFiles.length > 0;

        if (field.field_type === 'declaration' && isRequired) {
            if (value !== 'true' && value !== true) {
                return `${field.field_label} is required`;
            }
        } else if (isSignaturePadField(field) && isRequired) {
            const v = value || '';
            if (v.length < 30 || !String(v).startsWith('data:image')) {
                return `${field.field_label} is required`;
            }
        } else if (
            isRequired &&
            (!value || value === '' || (Array.isArray(value) && value.length === 0)) &&
            !hasStagedFiles
        ) {
            return `${field.field_label} is required`;
        }

        // Passport Expiry Date Validation: Cannot be before today
        if (field.field_key === 'passport_expiry_date' && value) {
            const expiryDate = new Date(value);
            const today = new Date();
            today.setHours(0, 0, 0, 0); // Normalize today to start of day
            
            if (expiryDate < today) {
                return 'Passport has expired. Please enter a valid expiry date.';
            }
        }

        if (field.field_type === 'email' && value) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) {
                return 'Please enter a valid email address';
            }
        }

        if (field.field_type === 'phone' && value) {
            const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
            if (!phoneRegex.test(value.replace(/\s/g, ''))) {
                return 'Please enter a valid phone number';
            }
        }

        return false;
    };

    const handleFieldBlur = (field, value) => {
        // Mark field as touched
        setTouchedFields(prev => new Set(prev).add(field.field_key));
        
        // Only validate and show error after field has been touched
        const error = validateField(field, value);
        setValidationErrors(prev => ({
            ...prev,
            [field.field_key]: error
        }));
    };

    const handleFileSelection = (fieldKey, files) => {
        if (!files || files.length === 0) return;
        
        setSelectedFiles(prev => ({
            ...prev,
            [fieldKey]: files
        }));

        // Clear validation error for this field
        setValidationErrors(prev => ({
            ...prev,
            [fieldKey]: false
        }));
    };

    const handleFileUpload = async (field) => {
        const files = selectedFiles[field.field_key];
        if (!files || files.length === 0) {
            toast({
                title: "No files selected",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        // Auto-save a draft registration if registrationId is missing
        if (!registrationId && onAutoSave) {
            const savedId = await onAutoSave();
            if (savedId) {
                setRegistrationId(savedId);
            } else {
                toast({
                    title: "Registration Required",
                    description: "Please fill in your basic information (name, email, etc.) first to enable file uploads.",
                    status: "warning",
                    duration: 5000,
                    isClosable: true,
                });
                return;
            }
        }
        
        // Allow uploads without registrationId for full certifications only
        const earlyUrlParams = new URLSearchParams(window.location.search);
        const earlyIsFullCertification = earlyUrlParams.get('full_certification');
        if (!registrationId && !earlyIsFullCertification) {
            toast({
                title: "Registration Required",
                description: "Please fill in your basic information (name, email, etc.) first to enable file uploads.",
                status: "warning",
                duration: 5000,
                isClosable: true,
            });
            return;
        }

        if (uploadingFiles.has(field.field_key)) {
            toast({
                title: "Please wait for the current upload to complete.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        try {
            setUploadingFiles(prev => new Set(prev).add(field.field_key));
            
            const studentName = formData.participant_name || 'Unknown';
            const sanitizedName = studentName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
            
            // Resolve IDs for full certification from formData first, then URL params as fallback
            const urlParams = new URLSearchParams(window.location.search);
            const isFullCertification = (formData.full_certification_id || urlParams.get('full_certification')) ? true : false;
            const fullCertificationId = formData.full_certification_id || urlParams.get('full_certification');
            const certificationRunId = formData.certification_run_id || urlParams.get('certification_run_id');
            
            console.log('File upload debug:', {
                isFullCertification,
                fullCertificationId,
                certificationRunId,
                registrationId,
                participantName: formData.participant_name
            });
            
            const uploadedFiles = [];
            const existingValue = getFieldValue(field.field_key);
            const existingFilesCount = Array.isArray(existingValue) ? existingValue.length : (existingValue ? 1 : 0);
            
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const fileExtension = file.name.split('.').pop() || '';
                const timestamp = new Date().toISOString().split('T')[0];
                
                // Dynamic identifier for file naming - use field_behaviors if available
                let fieldIdentifier = getDynamicFileIdentifier(field);
                
                // Add index to filename to make multiple uploads unique
                // Use existingFilesCount + i + 1 to ensure uniqueness even if uploading more files later
                const fileName = `${sanitizedName}_${fieldIdentifier}_${timestamp}_${existingFilesCount + i + 1}.${fileExtension}`;
                
                // Build a stable folder path
                let folderPath;
                if (isFullCertification) {
                    // Block if IDs not ready
                    if (!fullCertificationId || !certificationRunId) {
                        toast({
                            title: "Select certification run",
                            description: "Please select a certification run before uploading files.",
                            status: "warning",
                            duration: 4000,
                            isClosable: true,
                        });
                        break;
                    }
                    // Cache once per session
                    if (!fullCertFolderPathRef.current) {
                        fullCertFolderPathRef.current = `registrations/full__cert/${fullCertificationId}/${certificationRunId}/${sanitizedName}`;
                    }
                    folderPath = fullCertFolderPathRef.current;
                } else if (registrationId) {
                    // Individual course: stable per registration
                    folderPath = `registrations/${registrationId}`;
                } else {
                    // Should not happen due to earlier guard; double safety
                    toast({
                        title: "Registration Required",
                        description: "Please fill in your name first to create a registration record for file uploads.",
                        status: "warning",
                        duration: 5000,
                        isClosable: true,
                    });
                    break;
                }
                
                console.log('Using folder path:', folderPath);
                
                const filePath = `${folderPath}/${fileName}`;
                
                const { error } = await supabase.storage
                    .from('Registration_data')
                    .upload(filePath, file, {
                        cacheControl: '3600',
                        upsert: true
                    });

                if (error) {
                    console.error('Upload error:', error);
                    toast({
                        title: `Failed to upload ${file.name}`,
                        status: "error",
                        duration: 3000,
                        isClosable: true,
                    });
                    continue;
                }

                // Store the file path instead of the public URL
                uploadedFiles.push(filePath);
            }

            if (uploadedFiles.length > 0) {
                const existingValue = getFieldValue(field.field_key);
                let finalValue;
                
                if (field.field_type === 'file_multiple') {
                    const existingFiles = Array.isArray(existingValue) ? existingValue : (existingValue ? [existingValue] : []);
                    finalValue = [...existingFiles, ...uploadedFiles];
                } else {
                    finalValue = uploadedFiles[0];
                }
                
                handleFieldChange(field.field_key, finalValue);
                
                setSelectedFiles(prev => ({
                    ...prev,
                    [field.field_key]: null
                }));
                
                const input = document.getElementById(`file-input-${field.field_key}`);
                if (input) input.value = '';
            }
        } catch (error) {
            console.error('Error uploading files:', error);
            toast({
                title: "Failed to upload files. Please try again.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setUploadingFiles(prev => {
                const newSet = new Set(prev);
                newSet.delete(field.field_key);
                return newSet;
            });
        }
    };

    const getFieldValue = (fieldKey) => {
        if (formData[fieldKey] !== undefined && formData[fieldKey] !== null && formData[fieldKey] !== '') {
            return formData[fieldKey];
        }
        
        if (fieldKey.includes('.')) {
            const keys = fieldKey.split('.');
            let current = formData;
            for (const key of keys) {
                if (current && typeof current === 'object' && key in current) {
                    current = current[key];
                } else {
                    return '';
                }
            }
            return current || '';
        }
        
        return '';
    };

    const removeUploadedFile = (fieldKey, index) => {
        const currentValue = getFieldValue(fieldKey);
        if (Array.isArray(currentValue)) {
            const newValue = currentValue.filter((_, i) => i !== index);
            handleFieldChange(fieldKey, newValue.length > 0 ? newValue : '');
        } else {
            handleFieldChange(fieldKey, '');
        }
    };


    // Render education history component
    const renderEducationHistory = (field) => {
        const options = field.field_options || ["Institution", "Qualification", "Year"];
        const jsonArrayLabels = field.field_behaviors?.json_array_labels ?? {};
        
        // Get current entries or initialize with one empty entry
        const rawEntries = getFieldValue(field.field_key);
        let educationEntries = Array.isArray(rawEntries) ? rawEntries : [];
        
        // If no entries exist, initialize with one empty entry
        if (educationEntries.length === 0) {
            const emptyEntry = {};
            options.forEach(opt => {
                emptyEntry[opt.toLowerCase().replace(/\s+/g, '_')] = '';
            });
            // Initialize with one empty entry on first render
            setTimeout(() => {
                handleFieldChange(field.field_key, [emptyEntry]);
            }, 0);
            educationEntries = [emptyEntry];
        }
        
        const addEducationEntry = () => {
            const newEntry = {};
            options.forEach(opt => {
                newEntry[opt.toLowerCase().replace(/\s+/g, '_')] = '';
            });
            const updatedEntries = [...educationEntries, newEntry];
            handleFieldChange(field.field_key, updatedEntries);
        };
        
        const updateEducationEntry = (index, fieldName, fieldValue) => {
            const updatedEntries = [...educationEntries];
            updatedEntries[index] = { ...updatedEntries[index], [fieldName]: fieldValue };
            handleFieldChange(field.field_key, updatedEntries);
        };
        
        const removeEducationEntry = (index) => {
            const updatedEntries = educationEntries.filter((_, i) => i !== index);
            handleFieldChange(field.field_key, updatedEntries);
        };
        
        return (
            <Box>
                <VStack spacing={4} align="stretch">
                    {educationEntries.map((entry, index) => (
                        <Box key={index} p={4} border="1px solid" borderColor="gray.200" borderRadius="md" bg="white">
                            <VStack spacing={4} align="stretch">
                                {options.map(option => {
                                    const fieldName = option.toLowerCase().replace(/\s+/g, '_');
                                    const displayLabel = jsonArrayLabels[fieldName] ?? option;
                                    return (
                                        <FormControl key={fieldName}>
                                            <FormLabel fontSize="xs" fontWeight="medium" color="gray.600">{displayLabel}</FormLabel>
                                            <Input
                                                value={entry[fieldName] || ''}
                                                onChange={(e) => updateEducationEntry(index, fieldName, e.target.value)}
                                                placeholder={`Enter ${displayLabel.toLowerCase()}...`}
                                                size="sm"
                                                isReadOnly={isReadOnly}
                                                bg={isReadOnly ? "gray.50" : "white"}
                                            />
                                        </FormControl>
                                    );
                                })}
                                {/* Only show remove button if there's more than 1 entry */}
                                {!isReadOnly && educationEntries.length > 1 && (
                                    <Button
                                        size="sm"
                                        onClick={() => removeEducationEntry(index)}
                                        colorScheme="red"
                                        variant="outline"
                                        leftIcon={<DeleteIcon />}
                                    >
                                        Remove
                                    </Button>
                                )}
                            </VStack>
                        </Box>
                    ))}
                </VStack>
                
                {!isReadOnly && (
                    <HStack justify="flex-start" mt={4} width="100%">
                        <Button
                            onClick={addEducationEntry}
                            size="sm"
                            colorScheme="blue"
                            variant="outline"
                            leftIcon={<AddIcon />}
                            width={{ base: '100%', md: 'auto' }}
                            whiteSpace="normal"
                            height="auto"
                            py={2}
                            px={4}
                            textAlign="center"
                            lineHeight="tall"
                        >
                            Add more Education Record
                        </Button>
                    </HStack>
                )}
            </Box>
        );
    };

    // Render employment history component
    const renderEmploymentHistory = (field) => {
        const options = field.field_options || ["Organisation", "Position Held", "Duration"];
        const jsonArrayLabels = field.field_behaviors?.json_array_labels ?? {};
        
        // Get current entries or initialize with one empty entry
        const rawEntries = getFieldValue(field.field_key);
        let employmentEntries = Array.isArray(rawEntries) ? rawEntries : [];
        
        // If no entries exist, initialize with one empty entry
        if (employmentEntries.length === 0) {
            const emptyEntry = {};
            options.forEach(opt => {
                emptyEntry[opt.toLowerCase().replace(/\s+/g, '_')] = '';
            });
            // Initialize with one empty entry on first render
            setTimeout(() => {
                handleFieldChange(field.field_key, [emptyEntry]);
            }, 0);
            employmentEntries = [emptyEntry];
        }
        
        const addEmploymentEntry = () => {
            const newEntry = {};
            options.forEach(opt => {
                newEntry[opt.toLowerCase().replace(/\s+/g, '_')] = '';
            });
            const updatedEntries = [...employmentEntries, newEntry];
            handleFieldChange(field.field_key, updatedEntries);
        };
        
        const updateEmploymentEntry = (index, fieldName, fieldValue) => {
            const updatedEntries = [...employmentEntries];
            updatedEntries[index] = { ...updatedEntries[index], [fieldName]: fieldValue };
            handleFieldChange(field.field_key, updatedEntries);
        };
        
        const removeEmploymentEntry = (index) => {
            const updatedEntries = employmentEntries.filter((_, i) => i !== index);
            handleFieldChange(field.field_key, updatedEntries);
        };
        
        return (
            <Box>
                <VStack spacing={4} align="stretch">
                    {employmentEntries.map((entry, index) => (
                        <Box key={index} p={4} border="1px solid" borderColor="gray.200" borderRadius="md" bg="white">
                            <VStack spacing={4} align="stretch">
                                {options.map(option => {
                                    const fieldName = option.toLowerCase().replace(/\s+/g, '_');
                                    const displayLabel = jsonArrayLabels[fieldName] ?? option;
                                    return (
                                        <FormControl key={fieldName}>
                                            <FormLabel fontSize="xs" fontWeight="medium" color="gray.600">{displayLabel}</FormLabel>
                                            <Input
                                                value={entry[fieldName] || ''}
                                                onChange={(e) => updateEmploymentEntry(index, fieldName, e.target.value)}
                                                placeholder={`Enter ${displayLabel.toLowerCase()}...`}
                                                size="sm"
                                                isReadOnly={isReadOnly}
                                                bg={isReadOnly ? "gray.50" : "white"}
                                            />
                                        </FormControl>
                                    );
                                })}
                                {/* Only show remove button if there's more than 1 entry */}
                                {!isReadOnly && employmentEntries.length > 1 && (
                                    <Button
                                        size="sm"
                                        onClick={() => removeEmploymentEntry(index)}
                                        colorScheme="red"
                                        variant="outline"
                                        leftIcon={<DeleteIcon />}
                                    >
                                        Remove
                                    </Button>
                                )}
                            </VStack>
                        </Box>
                    ))}
                </VStack>
                
                {!isReadOnly && (
                    <HStack justify="flex-start" mt={4}>
                        <Button
                            onClick={addEmploymentEntry}
                            size="sm"
                            colorScheme="blue"
                            variant="outline"
                            leftIcon={<AddIcon />}
                        >
                            Add More Employment Record
                        </Button>
                    </HStack>
                )}
            </Box>
        );
    };

    const renderField = (field) => {
        const fieldValue = getFieldValue(field.field_key);
        // Only show error styling if field has been touched
        const isTouched = touchedFields.has(field.field_key);
        const fieldError = isTouched ? validationErrors[field.field_key] : null;
        const fieldId = `field-${field.id}`;

        const isRequired = getEffectiveRequiredStatus(field);
        const placeholder = getPlaceholder(field);
        const commonProps = {
            id: fieldId,
            value: fieldValue,
            onChange: (e) => handleFieldChange(field.field_key, e.target.value),
            onBlur: (e) => handleFieldBlur(field, e.target.value),
            required: isRequired,
            placeholder: placeholder || field.field_label,
            size: {base: "md", md: "md"},
            height: {base: "48px", md: "40px"}, // Larger touch target on mobile
            fontSize: {base: "16px", md: "14px"}, // Prevent zoom on iOS
            bg: isReadOnly ? "gray.50" : "white",
            isReadOnly: isReadOnly,
            isDisabled: isReadOnly,
            borderRadius: "md",
            _focus: isReadOnly ? {} : {
                borderColor: "blue.500",
                boxShadow: "0 0 0 2px rgba(66, 153, 225, 0.3)"
            }
        };

        // Searchable Select Override - use field_behaviors if available
        if (field.field_behaviors?.input_type_override === 'searchable_select' || 
            (field.field_key === 'personal_details_country_of_birth' && !field.field_behaviors)) {
            // Legacy fallback for personal_details_country_of_birth if field_behaviors not set
            const currentVal = countryOptions.find(opt => opt.label === fieldValue);
            return (
                <RegistrationSearchableSelect
                    {...commonProps}
                    value={currentVal}
                    onChange={(option) => handleFieldChange(field.field_key, option ? option.label : '')}
                    onBlur={() => handleFieldBlur(field, fieldValue)}
                    options={countryOptions}
                    placeholder="Search and select Country..."
                    isInvalid={!!fieldError}
                />
            );
        }

        if (corporateEnrollMode && isSignaturePadField(field)) {
            return (
                <CorporateSignatureField
                    field={field}
                    fieldId={fieldId}
                    fieldValue={fieldValue}
                    fieldError={!!fieldError}
                    isReadOnly={isReadOnly}
                    handleFieldChange={handleFieldChange}
                    handleFieldBlur={handleFieldBlur}
                />
            );
        }

        switch (field.field_type) {
            case 'text':
            case 'email':
            case 'phone':
                if (field.field_type === 'phone') {
                    return (
                        <PhoneInput
                            international
                            defaultCountry="SG"
                            disabled={isReadOnly}
                            value={fieldValue || ''}
                            onChange={(value) => handleFieldChange(field.field_key, value || '')}
                            inputComponent={Input}
                            style={{
                                display: 'flex',
                                alignItems: 'center'
                            }}
                            className="chakra-phone-input"
                            inputProps={{
                                isReadOnly: isReadOnly,
                                bg: isReadOnly ? "gray.50" : "white",
                                isInvalid: fieldError,
                                id: fieldId,
                                required: isRequired,
                                placeholder: placeholder || field.field_label,
                                size: "md",
                                _focus: isReadOnly ? {} : {
                                    borderColor: "blue.500",
                                    boxShadow: "0 0 0 1px blue.500"
                                }
                            }}
                        />
                    );
                }
                
                return (
                    <Input
                        {...commonProps}
                        type={field.field_type === 'email' ? 'email' : 'text'}
                        isInvalid={fieldError}
                    />
                );

            case 'number':
                return (
                    <Input
                        {...commonProps}
                        type="number"
                        onChange={(e) => handleFieldChange(field.field_key, parseFloat(e.target.value) || 0)}
                        isInvalid={fieldError}
                    />
                );

            case 'date':
                return (
                    <Input
                        {...commonProps}
                        type="date"
                        isInvalid={fieldError}
                    />
                );

            case 'textarea':
                return (
                    <Textarea
                        {...commonProps}
                        rows={3}
                        placeholder={getPlaceholder(field)}
                        isInvalid={fieldError}
                    />
                );

            case 'boolean':
                return (
                    <Box p={3} bg={isReadOnly ? "gray.100" : "gray.50"} borderRadius="md" border="1px solid" borderColor="gray.200">
                        <HStack>
                            <input
                                type="checkbox"
                                id={fieldId}
                                checked={!!fieldValue}
                                onChange={(e) => handleFieldChange(field.field_key, e.target.checked)}
                                disabled={isReadOnly}
                                style={{ marginRight: '8px' }}
                            />
                            <Text fontSize="sm" fontWeight="medium" color="gray.700">
                                {field.field_label}
                            </Text>
                        </HStack>
                    </Box>
                );

            case 'declaration_text': {
                const html = getDeclarationHtmlFromFieldOptions(field.field_options);
                return (
                    <Box
                        id={fieldId}
                        role="region"
                        aria-label={field.field_label}
                        sx={{ maxHeight: 'min(70vh, 520px)' }}
                        overflowY="auto"
                        p={{ base: 3, md: 4 }}
                        borderWidth="1px"
                        borderRadius="md"
                        borderColor="gray.200"
                        bg="gray.50"
                        className="additional-fields-declaration-html"
                        sx={{
                            '& table': { width: '100%', borderCollapse: 'collapse', fontSize: 'sm' },
                            '& th, & td': {
                                border: '1px solid',
                                borderColor: 'gray.200',
                                p: 2,
                                verticalAlign: 'top',
                            },
                            '& p': { mb: 2 },
                            '& p:last-child': { mb: 0 },
                            '& ul, & ol': { pl: 6, mb: 2 },
                        }}
                        dangerouslySetInnerHTML={html ? { __html: html } : undefined}
                    />
                );
            }

            case 'declaration': {
                const html = getDeclarationHtmlFromFieldOptions(field.field_options);
                const acknowledged = fieldValue === 'true' || fieldValue === true;
                return (
                    <VStack align="stretch" spacing={4}>
                        {html ? (
                            <Box
                                sx={{ maxHeight: 'min(70vh, 520px)' }}
                                overflowY="auto"
                                p={{ base: 3, md: 4 }}
                                borderWidth="1px"
                                borderRadius="md"
                                borderColor="gray.200"
                                bg="gray.50"
                                className="additional-fields-declaration-html"
                                sx={{
                                    '& table': { width: '100%', borderCollapse: 'collapse', fontSize: 'sm' },
                                    '& th, & td': {
                                        border: '1px solid',
                                        borderColor: 'gray.200',
                                        p: 2,
                                        verticalAlign: 'top',
                                    },
                                    '& p': { mb: 2 },
                                    '& p:last-child': { mb: 0 },
                                    '& ul, & ol': { pl: 6, mb: 2 },
                                }}
                                dangerouslySetInnerHTML={{ __html: html }}
                            />
                        ) : null}
                        <Box
                            p={3}
                            bg={isReadOnly ? 'gray.100' : 'gray.50'}
                            borderRadius="md"
                            borderWidth="1px"
                            borderColor={fieldError ? 'red.300' : 'gray.200'}
                        >
                            <Checkbox
                                id={fieldId}
                                isChecked={acknowledged}
                                isReadOnly={isReadOnly}
                                isDisabled={isReadOnly}
                                onChange={(e) =>
                                    handleFieldChange(field.field_key, e.target.checked ? 'true' : 'false')
                                }
                                onBlur={() => handleFieldBlur(field, acknowledged ? 'true' : 'false')}
                                colorScheme="blue"
                            >
                                <Text fontSize="sm" fontWeight="medium" color="gray.700">
                                    I have read and agree to the above
                                </Text>
                            </Checkbox>
                        </Box>
                    </VStack>
                );
            }

            case 'country': {
                const currentCountryVal = countryOptions.find(
                    opt => opt.label === fieldValue || opt.value === fieldValue
                ) || null;
                return (
                    <RegistrationSearchableSelect
                        id={fieldId}
                        value={currentCountryVal}
                        onChange={(option) => handleFieldChange(field.field_key, option ? option.label : '')}
                        onBlur={() => handleFieldBlur(field, fieldValue)}
                        options={countryOptions}
                        placeholder="Search and select country..."
                        isClearable
                        isDisabled={isReadOnly}
                        isInvalid={!!fieldError}
                    />
                );
            }

            case 'select':
                // Check for searchable select override
                if (field.field_behaviors?.input_type_override === 'searchable_select' || 
                    (field.field_key === 'personal_details_country_of_birth' && !field.field_behaviors)) {
                    // Legacy fallback for personal_details_country_of_birth if field_behaviors not set
                    const currentVal = countryOptions.find(opt => opt.label === fieldValue);
                    return (
                        <RegistrationSearchableSelect
                            {...commonProps}
                            value={currentVal}
                            onChange={(option) => handleFieldChange(field.field_key, option ? option.label : '')}
                            onBlur={() => handleFieldBlur(field, fieldValue)}
                            options={countryOptions}
                            placeholder="Search and select Country..."
                            isInvalid={!!fieldError}
                        />
                    );
                }

                const options = field.field_options || [];
                const isOtherOptionSelected = activeOtherFields.has(field.field_key) || (fieldValue && !options.includes(fieldValue) && fieldValue !== '');

                // For small number of options, show as buttons for better UX on both web and mobile
                if (options.length > 0 && options.length < 5 && !field.field_behaviors?.input_type_override) {
                    return (
                        <VStack align="stretch" spacing={2}>
                            <SimpleGrid 
                                columns={{ 
                                    base: 1, 
                                    sm: options.length === 2 ? 2 : (options.length === 3 ? 3 : 2), 
                                    md: Math.min(options.length, 4) 
                                }} 
                                spacing={2}
                            >
                                {options.map((option) => {
                                    const isOthers = option === 'Others' || option === 'Other';
                                    const isSelected = isOthers ? isOtherOptionSelected : fieldValue === option;
                                    
                                    return (
                                        <Button
                                            key={option}
                                            onClick={() => {
                                                if (isOthers) {
                                                    setActiveOtherFields(prev => new Set(prev).add(field.field_key));
                                                    handleFieldChange(field.field_key, ''); // Clear value to let user type
                                                } else {
                                                    setActiveOtherFields(prev => {
                                                        const next = new Set(prev);
                                                        next.delete(field.field_key);
                                                        return next;
                                                    });
                                                    handleFieldChange(field.field_key, option);
                                                }
                                            }}
                                            colorScheme={isSelected ? "blue" : "gray"}
                                            variant={isSelected ? "solid" : "outline"}
                                            isDisabled={isReadOnly}
                                            size="md"
                                            py={{ base: 4, md: 5 }}
                                            px={4}
                                            whiteSpace="normal"
                                            height="auto"
                                            textAlign="center"
                                            fontWeight={isSelected ? "bold" : "medium"}
                                            borderColor={isSelected ? "blue.500" : "gray.300"}
                                            _hover={!isReadOnly ? {
                                                bg: isSelected ? "blue.600" : "gray.100",
                                                borderColor: "blue.500"
                                            } : {}}
                                        >
                                            {field.field_behaviors?.option_labels?.[option] ?? option}
                                        </Button>
                                    );
                                })}
                            </SimpleGrid>
                            {isOtherOptionSelected && !isReadOnly && (
                                <Input
                                    placeholder={placeholder || `Please specify ${field.field_label.toLowerCase()}...`}
                                    value={fieldValue === 'Others' || fieldValue === 'Other' ? '' : fieldValue}
                                    onChange={(e) => handleFieldChange(field.field_key, e.target.value)}
                                    size="md"
                                    mt={1}
                                    autoFocus
                                    borderColor="blue.300"
                                    focusBorderColor="blue.500"
                                />
                            )}
                        </VStack>
                    );
                }

                return (
                    <VStack align="stretch" spacing={2}>
                        <Select
                            {...commonProps}
                            value={isOtherOptionSelected ? 'Others' : fieldValue}
                            placeholder={placeholder || `Select ${field.field_label}`}
                            isInvalid={!!fieldError}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val === 'Others' || val === 'Other') {
                                    setActiveOtherFields(prev => new Set(prev).add(field.field_key));
                                    handleFieldChange(field.field_key, ''); // Clear value to let user type
                                } else {
                                    setActiveOtherFields(prev => {
                                        const next = new Set(prev);
                                        next.delete(field.field_key);
                                        return next;
                                    });
                                    handleFieldChange(field.field_key, val);
                                }
                            }}
                        >
                            {options.map((option) => (
                                <option key={option} value={option}>
                                    {field.field_behaviors?.option_labels?.[option] ?? option}
                                </option>
                            ))}
                        </Select>
                        {isOtherOptionSelected && !isReadOnly && (
                            <Input
                                placeholder={placeholder || `Please specify ${field.field_label.toLowerCase()}...`}
                                value={fieldValue === 'Others' || fieldValue === 'Other' ? '' : fieldValue}
                                onChange={(e) => handleFieldChange(field.field_key, e.target.value)}
                                size="sm"
                                mt={1}
                                autoFocus
                            />
                        )}
                    </VStack>
                );

            case 'multiselect':
                const selectedValues = Array.isArray(fieldValue) ? fieldValue : [];
                const multiselectOptions = field.field_options || [];

                // For small number of options, show as toggle buttons
                if (multiselectOptions.length > 0 && multiselectOptions.length < 5) {
                    return (
                        <SimpleGrid 
                            columns={{ 
                                base: 1, 
                                sm: multiselectOptions.length === 2 ? 2 : (multiselectOptions.length === 3 ? 3 : 2), 
                                md: Math.min(multiselectOptions.length, 4) 
                            }} 
                            spacing={2}
                        >
                            {multiselectOptions.map((option) => {
                                const isSelected = selectedValues.includes(option);
                                return (
                                    <Button
                                        key={option}
                                        onClick={() => {
                                            if (isSelected) {
                                                handleFieldChange(field.field_key, selectedValues.filter(val => val !== option));
                                            } else {
                                                handleFieldChange(field.field_key, [...selectedValues, option]);
                                            }
                                        }}
                                        colorScheme={isSelected ? "blue" : "gray"}
                                        variant={isSelected ? "solid" : "outline"}
                                        isDisabled={isReadOnly}
                                        size="md"
                                        py={{ base: 4, md: 5 }}
                                        px={4}
                                        whiteSpace="normal"
                                        height="auto"
                                        textAlign="center"
                                        fontWeight={isSelected ? "bold" : "medium"}
                                        borderColor={isSelected ? "blue.500" : "gray.300"}
                                        leftIcon={isSelected ? <Icon as={() => <span>✓</span>} /> : undefined}
                                        _hover={!isReadOnly ? {
                                            bg: isSelected ? "blue.600" : "gray.100",
                                            borderColor: "blue.500"
                                        } : {}}
                                    >
                                        {field.field_behaviors?.option_labels?.[option] ?? option}
                                    </Button>
                                );
                            })}
                        </SimpleGrid>
                    );
                }

                return (
                    <Box>
                        {selectedValues.length > 0 && (
                            <Box mb={3} p={3} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
                                <HStack wrap="wrap" spacing={2}>
                                    {selectedValues.map((item, index) => (
                                        <Badge key={index} colorScheme="blue" px={2} py={1} borderRadius="md">
                                            {field.field_behaviors?.option_labels?.[item] ?? item}
                                            {!isReadOnly && (
                                                <IconButton
                                                    size="xs"
                                                    ml={2}
                                                    icon={<DeleteIcon />}
                                                    onClick={() => {
                                                        const newValue = selectedValues.filter((_, i) => i !== index);
                                                        handleFieldChange(field.field_key, newValue);
                                                    }}
                                                    variant="ghost"
                                                    colorScheme="blue"
                                                />
                                            )}
                                        </Badge>
                                    ))}
                                </HStack>
                            </Box>
                        )}
                        <Select
                            placeholder={placeholder || `Add ${field.field_label}`}
                            onChange={(e) => {
                                const selectedValue = e.target.value;
                                if (selectedValue && !selectedValues.includes(selectedValue)) {
                                    handleFieldChange(field.field_key, [...selectedValues, selectedValue]);
                                }
                            }}
                            size="md"
                            bg={isReadOnly ? "gray.50" : "white"}
                            isDisabled={isReadOnly}
                        >
                            {multiselectOptions.filter(option => !selectedValues.includes(option)).map((option) => (
                                <option key={option} value={option}>
                                    {field.field_behaviors?.option_labels?.[option] ?? option}
                                </option>
                            ))}
                        </Select>
                    </Box>
                );


            case 'json_array':
                // Special handling for education and employment history
                if (field.field_key === 'education') {
                    return renderEducationHistory(field);
                } else if (field.field_key === 'employment_history' || field.field_key === 'employment_record') {
                    return renderEmploymentHistory(field);
                }
                
                return (
                    <Box>
                        <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.700">
                            {field.field_label}
                            <Badge ml={2} colorScheme="yellow" size="sm">JSON Array</Badge>
                        </Text>
                        <Textarea
                            value={Array.isArray(fieldValue) ? JSON.stringify(fieldValue, null, 2) : ''}
                            onChange={(e) => {
                                try {
                                    const parsed = JSON.parse(e.target.value);
                                    handleFieldChange(field.field_key, parsed);
                                } catch {
                                    // Invalid JSON, keep as string for now
                                    handleFieldChange(field.field_key, e.target.value);
                                }
                            }}
                            rows={4}
                            fontFamily="mono"
                            fontSize="sm"
                            placeholder="Enter JSON array data..."
                            isInvalid={fieldError}
                            isReadOnly={isReadOnly}
                            bg={isReadOnly ? "gray.50" : "white"}
                        />
                    </Box>
                );

            case 'file':
            case 'file_multiple':
                return (
                    <FileUploadZone
                        field={field}
                        fieldValue={fieldValue}
                        isReadOnly={isReadOnly}
                        isUploading={uploadingFiles.has(field.field_key)}
                        selectedFilesForField={selectedFiles[field.field_key]}
                        onFileSelect={(files) => handleFileSelection(field.field_key, files)}
                        onRemoveStaged={(index) => {
                            const current = selectedFiles[field.field_key];
                            const newFiles = Array.from(current).filter((_, i) => i !== index);
                            const dt = new DataTransfer();
                            newFiles.forEach(f => dt.items.add(f));
                            setSelectedFiles(prev => ({ ...prev, [field.field_key]: dt.files }));
                        }}
                        onRemoveUploaded={(index) => removeUploadedFile(field.field_key, index)}
                        formData={formData}
                        toast={toast}
                    />
                );

            default:
                return (
                    <Input
                        {...commonProps}
                        type="text"
                        isInvalid={fieldError}
                    />
                );
        }
    };

    // Helper function to check if a field should be visible (must be declared before early returns — used by hooks below)
    const shouldShowField = (field) => {
        // If a registration template is set, only show fields included in it
        if (templateFieldIds !== null && !templateFieldIds.includes(field.id)) {
            return false;
        }

        if (corporateEnrollMode && !shouldShowOnCorporateEnrollForm(field)) {
            return false;
        }

        // Check field_behaviors first (new system)
        if (field.field_behaviors?.always_hidden === true) {
            return false;
        }

        // Legacy: hide declaration date / signing workflow ids; hide stray "signature" keys unless explicit pad field
        if (
            field.field_key === 'legal.declaration_date' ||
            field.field_key === 'sign_document_id' ||
            field.field_key === 'sign_requestid'
        ) {
            return false;
        }
        if (field.field_key?.includes('signature') && !isSignaturePadField(field)) {
            return false;
        }
        if (isSignaturePadField(field) && !corporateEnrollMode) {
            return false;
        }

        // Hide system fields here — Registration.js renders them in Step 1 unless corporate single-page mode
        if (field.is_original_field === true && !corporateEnrollMode) {
            return false;
        }

        // Compulsory-section custom fields are in Registration Step 1; show here only for corporate enrol
        if (field.section === 'Compulsory fields' && !corporateEnrollMode) {
            return false;
        }

        // If field is always required, show it
        if (field.is_required) {
            return true;
        }

        // If field has no conditional requirements, show it (it's optional)
        if (!field.conditional_requirements) {
            return true;
        }

        // If field has always_visible flag, show it regardless of condition
        // (it will be optional when condition is not met, required when condition is met)
        if (field.conditional_requirements.always_visible === true) {
            return true;
        }

        if (field.conditional_requirements?.required_when) {
            const triggerKey = field.conditional_requirements.required_when.field;
            if (!isTriggerFieldIncludedInTemplate(triggerKey)) {
                return true;
            }
        }

        // If field has conditional requirements, only show if condition is met
        return getEffectiveRequiredStatus(field);
    };

    useEffect(() => {
        if (!corporateEnrollMode || !corporateValidateTick) return;
        if (loading) return;
        if (error || additionalFields.length === 0) {
            corporateOnValidateComplete?.(false);
            return;
        }
        const errs = {};
        additionalFields.filter(shouldShowField).forEach((field) => {
            const fieldValue = getFieldValue(field.field_key);
            const err = validateField(field, fieldValue);
            if (err) errs[field.field_key] = err;
        });
        setValidationErrors((prev) => ({ ...prev, ...errs }));
        const ok = Object.keys(errs).length === 0;
        corporateOnValidateComplete?.(ok);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: run when tick or loading gate changes; reads latest formData from render
    }, [corporateValidateTick, corporateEnrollMode, loading, error]);

    if (loading) {
        return (
            <Center py={8}>
                <Spinner size="lg" color="brand.600" />
            </Center>
        );
    }

    if (error) {
        return (
            <Alert status="error" borderRadius="md">
                <AlertIcon />
                {error}
            </Alert>
        );
    }

    if (additionalFields.length === 0) {
        return null;
    }

    // Helper function to get dynamic label based on field_behaviors
    const getDynamicLabel = (field) => {
        if (!field.field_behaviors?.dynamic_label) {
            return field.field_label;
        }

        const { trigger_field, mappings } = field.field_behaviors.dynamic_label;
        const triggerValue = getFieldValue(trigger_field);
        
        return mappings[triggerValue] || mappings.default || field.field_label;
    };

    // Helper function to get dynamic file identifier based on field_behaviors
    const getDynamicFileIdentifier = (field) => {
        if (!field.field_behaviors?.dynamic_file_identifier) {
            return field.field_key.replace(/\./g, '_');
        }

        const { trigger_field, mappings } = field.field_behaviors.dynamic_file_identifier;
        const triggerValue = getFieldValue(trigger_field);
        
        return mappings[triggerValue] || mappings.default || field.field_key.replace(/\./g, '_');
    };

    // Helper function to get placeholder (supports both static and dynamic)
    const getPlaceholder = (field) => {
        // First check for static custom placeholder
        if (field.field_behaviors?.custom_placeholder) {
            return field.field_behaviors.custom_placeholder;
        }
        
        // Then check for dynamic placeholder from dynamic_label.placeholders
        if (field.field_behaviors?.dynamic_label?.placeholders) {
            const { trigger_field, placeholders } = field.field_behaviors.dynamic_label;
            const triggerValue = getFieldValue(trigger_field) || '';
            
            // Try to get placeholder for current trigger value, fallback to default
            return placeholders[triggerValue] || placeholders.default || '';
        }
        
        // Fallback to empty string (no placeholder)
        return '';
    };

    // Group fields by section and track section_order
    // IMPORTANT: Only custom fields (is_original_field = false) are shown here
    // System fields (is_original_field = true) are rendered in the main registration form
    // Custom fields moved to "Compulsory fields" section will appear here and save to additional_data
    const sectionOrderMap = {}; // Track section_order for each section
    const groupedFields = additionalFields
        .filter(shouldShowField)
        .reduce((acc, field) => {
            const originalSection = field.section || 'Additional Information';
            const simplifiedSection = mapToSimplifiedSection(originalSection);
            if (!acc[simplifiedSection]) {
                acc[simplifiedSection] = [];
            }
            acc[simplifiedSection].push(field);
            
            // Track the section_order (use the first field's section_order for each section)
            if (sectionOrderMap[simplifiedSection] === undefined) {
                sectionOrderMap[simplifiedSection] = field.section_order;
            }
            
            return acc;
        }, {});

    // Sort sections by section_order from database
    // "Compulsory fields" should always be first if it exists
    const finalSectionOrder = Object.keys(groupedFields).sort((a, b) => {
        // Compulsory fields always comes first
        if (a === 'Compulsory fields') return -1;
        if (b === 'Compulsory fields') return 1;
        
        const orderA = sectionOrderMap[a];
        const orderB = sectionOrderMap[b];
        
        // Handle NULL/undefined values - put them at the end
        if (orderA === null || orderA === undefined) return 1;
        if (orderB === null || orderB === undefined) return -1;
        
        // Sort by section_order ascending
        return orderA - orderB;
    });

    // Calculate overall progress (only count visible fields)
    const allFields = additionalFields.filter(shouldShowField);
    const completedOverall = allFields.filter(field => !isFieldEmpty(field)).length;
    const totalOverall = allFields.length;
    const progressPercentage = totalOverall > 0 ? Math.round((completedOverall / totalOverall) * 100) : 0;

    return (
        <Box>
            {/* All sections always expanded - no accordion collapsing */}
            <VStack spacing={{base: 4, md: 5}} align="stretch">
                {finalSectionOrder.map((sectionName) => {
                    const fields = groupedFields[sectionName];
                    if (!fields) return null;
                    const config = getSectionConfig(sectionName);
                    const isCompleted = fields.filter(field => !isFieldEmpty(field)).length === fields.length;
                    
                    return (
                        <Box 
                            key={sectionName} 
                            border="1px solid" 
                            borderColor={isCompleted ? "green.300" : config.borderColor} 
                            borderRadius="lg" 
                            overflow="hidden"
                            boxShadow={isCompleted ? "0 2px 8px rgba(72, 187, 120, 0.15)" : "sm"}
                            transition="all 0.2s"
                            bg="white"
                        >
                            {/* Section Header - Always visible, no expand/collapse */}
                            <Box 
                                bg={isCompleted ? "green.50" : config.bgColor}
                                py={{base: 3, md: 4}}
                                px={{base: 3, md: 5}}
                                borderBottom="1px solid"
                                borderColor={isCompleted ? "green.200" : config.borderColor}
                            >
                                <HStack spacing={{base: 2, md: 4}}>
                                    <Box 
                                        fontSize={{base: "xl", md: "2xl"}}
                                        p={{base: 1.5, md: 2}}
                                        bg="white" 
                                        borderRadius="md" 
                                        boxShadow="sm"
                                        minW={{base: "36px", md: "44px"}}
                                        textAlign="center"
                                    >
                                        {config.icon}
                                    </Box>
                                    <HStack spacing={2} flex="1" flexWrap="wrap">
                                        <Text 
                                            fontSize={{base: "sm", md: "md"}} 
                                            fontWeight="bold" 
                                            color={isCompleted ? "green.700" : `${config.color}.700`}
                                        >
                                            {sectionName}
                                        </Text>
                                        {isCompleted && (
                                            <Badge 
                                                colorScheme="green" 
                                                variant="solid" 
                                                px={1.5} 
                                                py={0.5} 
                                                fontSize="10px"
                                            >
                                                ✓ Complete
                                            </Badge>
                                        )}
                                    </HStack>
                                </HStack>
                            </Box>
                            
                            {/* Section Fields - Always shown */}
                            <Box 
                                pb={{base: 4, md: 6}} 
                                px={{base: 3, md: 5}} 
                                pt={{base: 3, md: 4}}
                                bg="white"
                            >
                                <VStack spacing={{base: 3, md: 4}} align="stretch">
                                    {fields.map((field) => {
                                        const isRequired = getEffectiveRequiredStatus(field);
                                        const fieldLabel = getDynamicLabel(field);

                                        // Only show errors for touched fields (user has interacted with them)
                                        const showError = touchedFields.has(field.field_key) && validationErrors[field.field_key];
                                        
                                        return (
                                            <FormControl key={field.id} isInvalid={showError}>
                                                <FormLabel 
                                                    mb={{base: 1.5, md: 2}} 
                                                    fontWeight="bold" 
                                                    color="gray.800" 
                                                    fontSize={{base: "sm", md: "md"}}
                                                >
                                                    <BilingualLabel label={fieldLabel} isRequired={isRequired} />
                                                </FormLabel>
                                                {renderField(field)}
                                                {showError && (
                                                    <Text color="red.500" fontSize={{base: "xs", md: "sm"}} mt={1}>
                                                        {validationErrors[field.field_key]}
                                                    </Text>
                                                )}
                                            </FormControl>
                                        );
                                    })}
                                </VStack>
                            </Box>
                        </Box>
                    );
                })}
            </VStack>
        </Box>
    );
};

export default AdditionalFields;